if (!customElements.get('product-portfolio')) {
  customElements.define(
    'product-portfolio',
    class ProductPortfolio extends HTMLElement {
      constructor() {
        super();

        this.cssClasses = {
          hovering: 'is-hovering',
          highlighted: 'is-highlighted',
          active: 'is-active',
          hidden: 'is-hidden',
        };
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get moneyFormat() {
        return this.dataset.moneyFormat || FoxTheme.settings.moneyFormat;
      }

      get items() {
        return Array.from(this.querySelectorAll('product-portfolio-item'));
      }

      get selectedItems() {
        return this.items.filter((item) => item.isSelected);
      }

      get hotspots() {
        return Array.from(this.querySelectorAll('.product-portfolio__hotspot'));
      }

      get imageContainer() {
        return this.querySelector('.product-portfolio__image');
      }

      get submitButton() {
        return this.querySelector('.product-portfolio__submit');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.totalPriceElement = this.querySelector('[data-total-price]');
        this.totalOriginalElement = this.querySelector('[data-total-original]');
        this.savingsElement = this.querySelector('[data-savings]');
        this.discountHintElement = this.querySelector('[data-discount-hint]');
        this.errorMessageElement = this.querySelector('.product-form__error-message');

        this.addEventListener('portfolio:item-change', this.updateSummary.bind(this), { signal });
        this.submitButton?.addEventListener('click', this.onSubmit.bind(this), { signal });
        this.addEventListener('click', this.onItemAddClick.bind(this), { signal });

        this.initHotspots(signal);
        this.updateSummary();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
      }

      formatMoney(cents) {
        return FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
      }

      // Mirrors the store's "Buy N, save X%" automatic discount (configured in the section settings).
      // The per-unit discount is floored to the cent like the cart does; keep in sync with the Liquid initial render.
      calculateTotals(items) {
        const minQuantity = Number(this.dataset.discountMin) || 0;
        const percent = Number(this.dataset.discountPercent) || 0;

        let subtotal = 0;
        let compareSavings = 0;
        let quantity = 0;
        let unitDiscounts = 0;

        items.forEach((item) => {
          subtotal += item.linePrice;
          compareSavings += item.lineSavings;
          quantity += item.quantity;
          unitDiscounts += Math.floor((item.currentVariant.price * percent) / 100) * item.quantity;
        });

        const enabled = minQuantity > 0 && percent > 0;
        const discount = enabled && quantity >= minQuantity ? unitDiscounts : 0;

        return {
          subtotal,
          discount,
          total: subtotal - discount,
          savings: compareSavings + discount,
          quantity,
          remaining: enabled && quantity < minQuantity ? minQuantity - quantity : 0,
        };
      }

      updateSummary() {
        const selectedItems = this.selectedItems;
        const { subtotal, discount, total, savings, remaining } = this.calculateTotals(selectedItems);

        // Selected items show their discounted unit price once the quantity discount is in effect
        const itemDiscountPercent = discount > 0 ? Number(this.dataset.discountPercent) || 0 : 0;
        this.items.forEach((item) => item.applyDiscount?.(itemDiscountPercent));

        if (this.totalPriceElement) this.totalPriceElement.innerHTML = this.formatMoney(total);

        if (this.totalOriginalElement) {
          this.totalOriginalElement.hidden = discount === 0;
          if (discount > 0) this.totalOriginalElement.innerHTML = this.formatMoney(subtotal);
        }

        // Fewer than 2 items selected and the discount not reached: the hint takes the slot, otherwise the savings text does
        const hintMode = selectedItems.length < 2 && remaining > 0;

        if (this.savingsElement) {
          const savingsText = this.dataset.savingsText || '';
          const showSavings = !hintMode && savings > 0 && savingsText !== '';
          this.savingsElement.hidden = !showSavings;
          if (showSavings) {
            this.savingsElement.innerHTML = savingsText.replace('[amount]', this.formatMoney(savings));
          }
        }

        if (this.discountHintElement) {
          const hintText = this.dataset.discountHintText || '';
          const showHint = hintMode && hintText !== '';
          this.discountHintElement.hidden = !showHint;
          if (showHint) this.discountHintElement.textContent = hintText.replace('[count]', remaining);
        }

        this.toggleSubmit(selectedItems.length > 0);
      }

      toggleSubmit(enabled) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', !enabled);
        this.submitButton.setAttribute('aria-disabled', String(!enabled));
      }

      createBundleId() {
        const random = Math.random().toString(36).slice(2, 6);
        return `${Date.now().toString(36)}${random}`.toUpperCase();
      }

      getCartItems(items = this.selectedItems) {
        const properties = {
          _bundle_id: this.createBundleId(),
          _bundle_name: this.dataset.bundleName,
        };

        return items.map((item) => ({
          id: item.currentVariant.id,
          quantity: item.quantity,
          properties,
        }));
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.addToCart(this.getCartItems(), this.submitButton);
      }

      // Per-item cart button: adds just that item (with its bundle properties), whatever its checkbox state.
      onItemAddClick(event) {
        const button = event.target.closest('.product-portfolio-item__add');
        if (!button || button.disabled) return;
        event.preventDefault();

        const item = button.closest('product-portfolio-item');
        if (!item?.canAdd) return;

        this.addToCart(this.getCartItems([item]), button);
      }

      addToCart(items, triggerButton) {
        if (items.length === 0) return;

        if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType != 'drawer') {
          this.submitForm(items);
          return;
        }

        this.showErrorMessage();
        this.toggleLoading(true, triggerButton);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showErrorMessage(parsedState.description);
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } })
              );
              return;
            }

            const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
            this.cartDrawer && this.cartDrawer.show(triggerButton);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.toggleLoading(false, triggerButton);
          });
      }

      // Fallback for cart page / non-drawer cart types: a regular form post keeps the line item properties.
      submitForm(items) {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = FoxTheme.routes.cart_add_url;

        const addField = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };

        items.forEach((item, index) => {
          addField(`items[${index}][id]`, item.id);
          addField(`items[${index}][quantity]`, item.quantity);
          Object.entries(item.properties).forEach(([key, value]) => {
            addField(`items[${index}][properties][${key}]`, value);
          });
        });

        document.body.appendChild(form);
        form.submit();
      }

      showErrorMessage(errorMessage = false) {
        if (!this.errorMessageElement) {
          errorMessage && alert(errorMessage);
          return;
        }
        this.errorMessageElement.hidden = !errorMessage;
        if (errorMessage) this.errorMessageElement.textContent = errorMessage;
      }

      toggleLoading(status, button = this.submitButton) {
        if (button === this.submitButton) {
          button.classList.toggle('btn--loading', status);
          if (status) {
            this.toggleSubmit(false);
          } else {
            this.updateSummary();
          }
          return;
        }

        button.classList.toggle('is-loading', status);
        button.toggleAttribute('aria-busy', status);
        button.disabled = status;
        if (!status) button.closest('product-portfolio-item')?.syncAddButton();
      }

      /* Hotspots */

      initHotspots(signal) {
        if (this.hotspots.length === 0) return;

        this.hotspots.forEach((hotspot) => {
          hotspot.addEventListener('mouseenter', () => this.highlight(hotspot.dataset.itemId), { signal });
          hotspot.addEventListener('mouseleave', () => this.highlight(null), { signal });
          hotspot.addEventListener('focus', () => this.highlight(hotspot.dataset.itemId), { signal });
          hotspot.addEventListener('blur', () => this.highlight(null), { signal });
          hotspot.addEventListener('click', () => this.onHotspotClick(hotspot), { signal });
        });

        this.items.forEach((item) => {
          item.addEventListener('mouseenter', () => this.highlight(item.dataset.itemId), { signal });
          item.addEventListener('mouseleave', () => this.highlight(null), { signal });
        });

        this.positionHotspots();
        if (this.imageContainer) {
          this.resizeObserver = new ResizeObserver(() => this.positionHotspots());
          this.resizeObserver.observe(this.imageContainer);
        }
      }

      onHotspotClick(hotspot) {
        const item = this.items.find((element) => element.dataset.itemId === hotspot.dataset.itemId);
        if (!item) return;
        this.highlight(hotspot.dataset.itemId);
        if (FoxTheme.config.mqlMobile || FoxTheme.config.mqlTablet) {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      highlight(itemId) {
        this.classList.toggle(this.cssClasses.hovering, Boolean(itemId));
        this.items.forEach((item) => {
          item.classList.toggle(this.cssClasses.highlighted, item.dataset.itemId === itemId);
        });
        this.hotspots.forEach((hotspot) => {
          hotspot.classList.toggle(this.cssClasses.active, hotspot.dataset.itemId === itemId);
        });
      }

      // The image is rendered with object-fit: cover, so percentage coordinates are mapped onto
      // the visible (possibly cropped) image area to keep hotspots pinned to the same spot.
      positionHotspots() {
        const container = this.imageContainer;
        if (!container) return;

        const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile).matches;
        const ratio = parseFloat(isMobile ? container.dataset.ratioMobile : container.dataset.ratio);
        const { width, height } = container.getBoundingClientRect();
        if (!ratio || !width || !height) return;

        let renderWidth = width;
        let renderHeight = width / ratio;
        if (renderHeight < height) {
          renderHeight = height;
          renderWidth = height * ratio;
        }
        const offsetX = (width - renderWidth) / 2;
        const offsetY = (height - renderHeight) / 2;

        this.hotspots.forEach((hotspot) => {
          const x = parseFloat(isMobile ? hotspot.dataset.xMobile : hotspot.dataset.x);
          const y = parseFloat(isMobile ? hotspot.dataset.yMobile : hotspot.dataset.y);
          const left = offsetX + (renderWidth * x) / 100;
          const top = offsetY + (renderHeight * y) / 100;

          hotspot.style.setProperty('--hotspot-left', `${left}px`);
          hotspot.style.setProperty('--hotspot-top', `${top}px`);
          hotspot.classList.toggle(this.cssClasses.hidden, left < 0 || left > width || top < 0 || top > height);
        });
      }
    }
  );
}

if (!customElements.get('product-portfolio-item')) {
  customElements.define(
    'product-portfolio-item',
    class ProductPortfolioItem extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.checkbox = this.querySelector('.product-portfolio-item__checkbox');
        this.quantityInput = this.querySelector('.quantity__input');
        this.optionSelects = Array.from(this.querySelectorAll('select[data-option-index]'));
        this.currentVariant =
          this.variants.find((variant) => variant.id === Number(this.dataset.variantId)) || this.variants[0];

        this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this), { signal });
        // quantity-input may adjust the value after dispatching change, so read it once that settles.
        this.quantityInput.addEventListener('change', () => queueMicrotask(() => this.onQuantityChange()), { signal });
        this.optionSelects.forEach((select) =>
          select.addEventListener('change', this.onOptionChange.bind(this), { signal })
        );

        this.addButton = this.querySelector('.product-portfolio-item__add');

        this.updateOptionAvailability();
        this.updateQuantityRules();
        this.selectByDefault();
      }

      // Items start selected at their minimum quantity (the server render does the same for the first paint).
      selectByDefault() {
        if (this.currentVariant?.available && this.quantity === 0) {
          this.checkbox.checked = true;
          this.setQuantity(this.minQuantity);
        }
        this.notifyChange();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get portfolio() {
        return this.closest('product-portfolio');
      }

      get quantity() {
        return parseInt(this.quantityInput.value) || 0;
      }

      get isSelected() {
        return Boolean(this.checkbox.checked && this.quantity > 0 && this.currentVariant?.available);
      }

      get canAdd() {
        return Boolean(this.currentVariant?.available && this.quantity > 0);
      }

      get linePrice() {
        return this.currentVariant.price * this.quantity;
      }

      get lineSavings() {
        const { price, compare_at_price } = this.currentVariant;
        return Math.max(compare_at_price - price, 0) * this.quantity;
      }

      get minQuantity() {
        return Math.max(this.currentVariant?.min || 1, 1);
      }

      get maxQuantity() {
        return this.currentVariant?.max ?? null;
      }

      setQuantity(quantity) {
        this.quantityInput.value = quantity;
        this.quantityInput.closest('quantity-input')?.validateQtyRules?.();
      }

      onCheckboxChange() {
        if (this.checkbox.checked && this.quantity === 0) {
          this.setQuantity(this.minQuantity);
        } else if (!this.checkbox.checked) {
          this.setQuantity(0);
        }
        this.notifyChange();
      }

      onQuantityChange() {
        const available = Boolean(this.currentVariant?.available);
        let quantity = available ? Math.max(this.quantity, 0) : 0;
        if (this.maxQuantity !== null) quantity = Math.min(quantity, this.maxQuantity);
        if (quantity !== this.quantity || this.quantityInput.value === '') this.setQuantity(quantity);

        this.checkbox.checked = quantity > 0 && available;
        this.notifyChange();
      }

      onOptionChange(event) {
        const changedIndex = Number(event.target.dataset.optionIndex);
        const selectedOptions = this.optionSelects.map((select) => select.value);
        const matchesOptions = (variant) => variant.options.every((value, index) => value === selectedOptions[index]);

        let variant = this.variants.find(matchesOptions);

        // Keep the chosen value and fall back to an available combination for the other options.
        if (!variant || !variant.available) {
          const fallback = this.variants.find(
            (item) => item.available && item.options[changedIndex] === selectedOptions[changedIndex]
          );
          if (fallback) {
            variant = fallback;
            this.optionSelects.forEach((select, index) => (select.value = fallback.options[index]));
          }
        }

        this.updateVariant(variant || null);
      }

      updateVariant(variant) {
        this.currentVariant = variant;
        const available = Boolean(variant?.available);

        this.classList.toggle('is-unavailable', !available);
        this.checkbox.disabled = !available;
        this.quantityInput.disabled = !available;
        this.querySelector('[data-sold-out]').hidden = available;

        if (!available) {
          this.checkbox.checked = false;
          this.setQuantity(0);
        }

        if (variant) {
          this.dataset.variantId = variant.id;
          this.updatePrice(variant);
          this.updateImage(variant);
          this.updateQuantityRules();
        }

        this.notifyChange();
      }

      applyDiscount(percent) {
        this.discountPercent = percent;
        if (this.currentVariant) this.updatePrice(this.currentVariant);
      }

      updatePrice(variant) {
        const portfolio = this.portfolio;
        const format = (cents) => (portfolio ? portfolio.formatMoney(cents) : FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat));

        // Only selected items get the quantity discount (same floored per-unit amount as the cart).
        const percent = this.isSelected ? this.discountPercent || 0 : 0;
        const price = variant.price - Math.floor((variant.price * percent) / 100);
        const reference = Math.max(variant.compare_at_price, variant.price);
        const hasCompare = reference > price;

        this.querySelector('[data-price]').innerHTML = format(price);

        const compareElement = this.querySelector('[data-compare-price]');
        compareElement.hidden = !hasCompare;
        if (hasCompare) compareElement.innerHTML = format(reference);

        const savingElement = this.querySelector('[data-saving]');
        savingElement.hidden = !hasCompare;
        const savingValueElement = savingElement.querySelector('[data-sale-value]');
        if (hasCompare && savingValueElement) {
          savingValueElement.innerHTML = format(reference - price);
        }
      }

      updateImage(variant) {
        if (!variant.image) return;
        const image = this.querySelector('.product-portfolio-item__media img');
        if (!image) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = variant.image;
      }

      updateQuantityRules() {
        const variant = this.currentVariant;
        if (!variant) return;

        this.quantityInput.step = variant.step || 1;
        this.quantityInput.dataset.min = variant.min || 1;
        if (this.maxQuantity !== null) {
          this.quantityInput.max = this.maxQuantity;
          if (this.quantity > this.maxQuantity) this.setQuantity(this.maxQuantity);
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.closest('quantity-input')?.validateQtyRules?.();
      }

      // Disable option values that have no purchasable variant at all.
      updateOptionAvailability() {
        this.optionSelects.forEach((select, index) => {
          Array.from(select.options).forEach((option) => {
            option.disabled = !this.variants.some(
              (variant) => variant.available && variant.options[index] === option.value
            );
          });
        });
      }

      syncAddButton() {
        if (this.addButton) this.addButton.disabled = !this.canAdd;
      }

      notifyChange() {
        this.syncAddButton();
        this.dispatchEvent(new CustomEvent('portfolio:item-change', { bubbles: true }));
      }
    }
  );
}
